import type {
  GeneratedVideo,
  GeneratedVideoWithHistory,
  ReactFlowNodeLike,
} from '../../../../generated/nodeDataTypes'
import { generatedVideoPath, getPickedVideoIndex } from './designerUtils'

export interface PickedVideoAsset {
  output: GeneratedVideoWithHistory
  history: GeneratedVideo[]
  pickedIndex: number
  entry: GeneratedVideo
  path: string
}

function isVideoHistoryOutput(value: object): value is GeneratedVideoWithHistory {
  if ('history' in value && value.history !== undefined && !Array.isArray(value.history)) {
    return false
  }
  if ('picked_index' in value && value.picked_index !== undefined && typeof value.picked_index !== 'number') {
    return false
  }
  if (
    'picked_variant_id' in value &&
    value.picked_variant_id !== undefined &&
    value.picked_variant_id !== null &&
    typeof value.picked_variant_id !== 'string'
  ) {
    return false
  }
  return true
}

/**
 * Read the standard video-history output from any video-producing canvas node.
 * Keeping this structural means consumers do not need a separate branch
 * whenever another video-producing node is introduced.
 */
export function getNodeVideoOutput(
  node: ReactFlowNodeLike | undefined,
): GeneratedVideoWithHistory | undefined {
  const data = node?.data
  if (!data || typeof data !== 'object' || !('out_video' in data)) return undefined

  const output = data.out_video
  if (!output || typeof output !== 'object') return undefined
  if (!isVideoHistoryOutput(output)) return undefined
  return output
}

/** Resolve the currently selected video and retain its complete history. */
export function resolvePickedVideoAsset(
  node: ReactFlowNodeLike | undefined,
): PickedVideoAsset | undefined {
  const output = getNodeVideoOutput(node)
  if (!output) return undefined

  const history = output.history ?? []
  const pickedIndex = getPickedVideoIndex(output)
  const entry = history[pickedIndex]
  const path = generatedVideoPath(entry)
  if (!entry || !path) return undefined

  return { output, history, pickedIndex, entry, path }
}
